import {ReactElement, useEffect, useRef, useState} from "react";
import {useFieldArray} from "react-hook-form";
import {useParams} from "react-router-dom";
import {styled} from "styled-components";

import {createContrat, updateContrat} from "@/Api/contrat";
import {deleteFichier, downloadFichier} from "@/Api/fichier";
import IconAdd from "@/assets/iconAdd.svg?react";
import IconContrat from "@/assets/iconContrat.svg?react";
import IconCross from "@/assets/iconCross.svg?react";
import IconPdf from "@/assets/iconPDF.svg?react";
import IconTrash from "@/assets/iconTrash.svg?react";
import {BlocContent} from "@/components/BlocContent/BlocContent";
import {Button} from "@/components/Button/Button";
import {ErrorMessage} from "@/components/ErrorMessage/ErrorMessage";
import {FichierEnvoye} from "@/components/FichierEnvoye/FichierEnvoye";
import {Input} from "@/components/Formulaire/Input/Input";
import {Textarea} from "@/components/Formulaire/Input/Textarea";
import {KpiElementList} from "@/components/Kpi/KpiElementList/KpiElementList";
import Loader from "@/components/Loader/Loader";
import {Title} from "@/components/Title/Title";
import {Tooltips} from "@/components/Tooltips/Tooltips";
import {AppContext} from "@/context/AppContext";
import {FournisseurContext} from "@/context/FournisseurContext";
import {FournisseurContextType} from "@/context/FournisseurContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {breakpoints, quicksandMedium, quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";
import {OptionNameValue} from "@/ts/formulaire";
import {Kpi, KpiEnCours} from "@/ts/kpi";

import {FournisseurHeader} from "../FournisseurHeader/FournisseurHeader";
import {UploadContrat} from "../FournisseurUploadContrat/FournisseurUploadContrat";
// Define FormData type to match the form structure
type AddedKpi = {
  kpi: Kpi;
  informations?: string;
  objectif?: number;
};
type FormData = {
  addedKpi: AddedKpi[];
  // add other fields if needed
};

const ButtonStyled = styled(Button)`
  padding: 15px 10px;
  margin: 50px auto 0;
`;

const ButtonNoClicableStyled = styled(Button)`
  padding: 15px 10px;
  margin: 50px auto 0;
  box-shadow: none;
  &:hover,
  &:not(:disabled):active {
    transition: none !important;
    transform: none !important;
    cursor: initial;
    box-shadow: none;
  }
`;

const StyledFooter = styled.div`
  width: 80%;
  margin: 20px auto 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  align-items: center;
  row-gap: 20px;
`;

const BlocInputKpi = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  & textarea {
    flex: 3;
    min-width: 400px;
    height: 17px;
    min-height: 17px;
    resize: vertical;
  }
  & input {
    max-width: 200px;
    min-width: 150px;
    flex: none;
    align-self: flex-start;
  }
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    & textarea {
      min-width: 180px;
      width: calc(100% - 1.25rem);
      min-height: 34px;
      padding: 0.625rem;
    }
    & input {
      width: calc(100% - 1.25rem);
    }
  }
`;
const KpiElementListContent = styled.div`
  display: flex;
  flex-wrap: nowrap;
  margin: 5px 0;
  & button {
    align-self: center;
    width: 135px;
    justify-content: center;
  }
  @media (max-width: ${breakpoints.lg}) {
    & button {
      width: auto;
      & span {
        display: none;
      }
    }
  }

  @media (max-width: ${breakpoints.sm}) {
    & button {
      padding: 7px 10px;
    }
  }
`;

const KpiAddedList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.625rem;
  & button {
    align-self: center;
    width: 135px;
    justify-content: center;
  }
  @media (max-width: ${breakpoints.lg}) {
    & button {
      width: auto;
      & span {
        display: none;
      }
    }
  }
  @media (max-width: ${breakpoints.sm}) {
    & button {
      padding: 7px 10px;
    }
  }
`;

const KpiAddedListContent = styled.div`
  width: calc(80% - 1.25rem);
  margin: 1.25rem auto 0px;
  padding: 0px 0.625rem;
  column-gap: 1.25rem;
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  border: 2px solid ${({theme}) => theme.colors.darkPrimary};
  & h3 {
    margin: 0;
    ${quicksandSemiBold}
    font-size: 1.25rem;
    color: ${({theme}) => theme.colors.mediumPrimary};
  }
  @media (max-width: ${breakpoints.md}) {
    width: 100%;
    margin: 1.25rem 0 0 0;
    flex-direction: column;
    padding: 0;
    border: 0;
    & h3 {
      font-size: 1rem;
    }
  }
`;
const KpiAddedInfoComplementaire = styled.p`
  color: ${({theme}) => theme.colors.mediumPrimary};
  font-size: 1rem;
  ${quicksandMedium}
`;
const KpiAddedElementContentLeft = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${breakpoints.md}) {
    padding: 10px 5px;
    border-left: 2px solid ${({theme}) => theme.colors.darkPrimary};
    border-top: 2px solid ${({theme}) => theme.colors.darkPrimary};

    border-right: 2px solid ${({theme}) => theme.colors.darkPrimary};
  }
`;

const KpiAddedName = styled.div`
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  & p {
    margin-left: 20px;
  }
  @media (max-width: ${breakpoints.md}) {
    padding: 10px 5px;
  }
`;

const KpiAddedObjectif = styled.div`
  height: calc(100% - 20px);
  width: auto;
  padding: 10px 10px 10px 20px;
  border-left: 2px solid ${({theme}) => theme.colors.darkPrimary};
  & h6,
  & p {
    margin: 0;
    text-align: center;
    color: ${({theme}) => theme.colors.lightPrimary};
    font-size: 1rem;
    ${quicksandSemiBold}
  }
  & p {
    display: flex;
    height: calc(100% - 30px);
    align-items: center;
    justify-content: center;
  }
  @media (max-width: ${breakpoints.md}) {
    border-left: 0px;
    border-top: 2px solid ${({theme}) => theme.colors.lightPrimary};
    background-color: ${({theme}) => theme.colors.lightPrimary};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 20px 0 20px 0;
    & p,
    & h6 {
      font-size: 1.25rem;
      color: white;
    }
  }
`;

export function FournisseurContentNoContrat(): ReactElement {
  // Ref pour le scroll vers le bloc de téléchargement du contrat lors de l'enregistrement du contrat
  const downloadContratRef = useRef<HTMLDivElement>(null);

  const {fournisseurAndCampagne, setFournisseurAndCampagne} =
    useGetContext<FournisseurContextType>(
      FournisseurContext,
      "FournisseurContext"
    );
  if (!fournisseurAndCampagne) {
    return <div>No fournisseur and campagne</div>;
  }

  const {register, handleSubmit, control, getValues, setValue, reset} =
    useGetContext<HookFormContextType>(HookFormContext, "HookFormContext");

  const {id: fournisseurId} = useParams();
  const {API, userType} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );
  const {currentContremarque} = useGetContext<UserParamContextType>(
    UserParamContext,
    "UserParamContext"
  );

  const {fournisseur, campagne, contrat} = fournisseurAndCampagne;

  const {
    fields: fieldAddedKpi,
    append: appendAddedKpi,
    remove: removeAddedKpi,
  } = useFieldArray<FormData, "addedKpi">({
    control,
    name: "addedKpi",
  });

  const handleGetAddedKpi = (contrat: Contrat | null): void => {
    if (contrat) {
      reset({
        addedKpi: [],
      });
      (contrat.campagne.kpis as KpiEnCours[])
        .filter(kpi => kpi.objectif > 0)
        .forEach(kpi => {
          appendAddedKpi({
            kpi: kpi,
            informations: kpi.informations,
            objectif: kpi.objectif,
          });
        });
      // mettre les fichiers du contrat dans le formulaire
      contrat.fichiers.forEach(fichier => {
        setValue(`${fichier.classe}-isUploaded`, true);
        setValue(`${fichier.classe}-fichier_id`, fichier.fichier_id);
        setValue(`${fichier.classe}-upload`, [
          new File([""], fichier.nom, {
            type: fichier.type || "application/pdf",
          }),
        ]);
      });
      // mettre les fichiers du contrat dans le formulaire ICI
    }
  };

  // si on récupère un contrat on utilise les kpis de la campagne du contrat pour remplir le formulaire.
  useEffect(() => {
    handleGetAddedKpi(contrat);
  }, [contrat]);
  const currentContratIsEncours: boolean = contrat?.statut === "encours";

  //La valeur par defaut permat de savoir s'il on doit arriver ou non en mode edition du contrat.
  const [isCreationContratStatut, setIsCreationContratStatut] =
    useState<boolean>(currentContratIsEncours);
  const handleStartAdhesion = (): void => {
    setIsCreationContratStatut(true);
  };

  const DownloadContratLoadingState = useLoadingState();
  useEffect(() => {
    const kpiAdded = localStorage.getItem(
      `KPI_added_${campagne?.campagne_id}_${fournisseur?.fournisseur_id}_${currentContremarque?.value}`
    );

    if (kpiAdded) {
      setValue("addedKpi", JSON.parse(kpiAdded));
      setIsCreationContratStatut(true);
    }
  }, []);

  const handleRemoveFile = async (
    fichier_id: number,
    typeDocument: "contrat-signe" | "annexe"
  ): Promise<void> => {
    try {
      await deleteFichier(API, fichier_id);
      setValue(`${typeDocument}-isUploaded`, false);
      setValue(`${typeDocument}-fichier_id`, undefined);
      setValue(`${typeDocument}-upload`, []);
    } catch (error: any) {
      throw new Error(error?.message);
    }
  };
  const KpiButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // liste des kpi de la campagne.
  const KpiList: ReactElement[] | undefined = campagne?.kpis
    ?.filter(elementKpi => {
      const finedIndex = fieldAddedKpi.findIndex(kpi => {
        return kpi?.kpi.kpi_id?.toString() === elementKpi.kpi_id?.toString();
      });
      return finedIndex === -1;
    })
    .map((elementKPI, index) => {
      const errorText = getValues(`KpiError.${elementKPI.kpi_id}.message`);
      // Fonction pour déclencher le bouton avec Entrée
      const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === "Enter") {
          e.preventDefault();
          KpiButtonRefs.current[elementKPI.kpi_id as string]?.click();
        }
      };
      return (
        <KpiElementListContent
          key={`${elementKPI.nom}-${index}`}
          data-testid="KpiElementListContent">
          <KpiElementList kpi={elementKPI} data-testid="KpiElementList">
            {/*Affiche les input quand on est en création de contrat
            Seul les adhérents peuvent créer un contrat*/}
            {isCreationContratStatut && userType === "Adherent" ? (
              <>
                <BlocInputKpi data-testid="BlocInputKpi">
                  <Textarea
                    placeholder="Informations complémentaire"
                    title="Informations complémentaire"
                    {...register(
                      `Kpi.${elementKPI.kpi_id}.informations`
                    )}></Textarea>
                  <Input
                    placeholder="Valeur objectif *"
                    title="Valeur objectif"
                    type="number"
                    min="0"
                    {...register(`Kpi.${elementKPI.kpi_id}.objectif`)}
                    onKeyDown={handleKeyDown}></Input>
                </BlocInputKpi>
                {errorText ? <ErrorMessage text={errorText} /> : null}
              </>
            ) : null}
          </KpiElementList>
          {/* Si on est en création de contrat et que l'utilisateur est un adhérent, on affiche le bouton d'ajout 
          Seul les adhérents peuvent créer un contrat */}
          {isCreationContratStatut && userType === "Adherent" ? (
            <Button
              label="Ajouter"
              icon={IconAdd}
              ref={el =>
                void (KpiButtonRefs.current[elementKPI.kpi_id as string] = el)
              }
              type="button"
              onClick={() => {
                const infoComplementaire = getValues(
                  `Kpi.${elementKPI.kpi_id}.informations`
                );
                const objectif = getValues(`Kpi.${elementKPI.kpi_id}.objectif`);
                if (objectif && objectif > 0) {
                  appendAddedKpi({
                    kpi: elementKPI,
                    informations: infoComplementaire,
                    objectif: objectif,
                  });
                  setValue(`KpiError.${elementKPI.kpi_id}.message`, undefined);
                  // on met a jour le local storage si on aucun contrat n'est encore créer
                  if (!currentContratIsEncours) {
                    localStorage.setItem(
                      `KPI_added_${campagne.campagne_id}_${fournisseur?.fournisseur_id}_${currentContremarque?.value}`,
                      JSON.stringify(getValues("addedKpi"))
                    );
                  }
                } else {
                  setValue(
                    `KpiError.${elementKPI.kpi_id}.message`,
                    "Un objectif est necessaire"
                  );
                }
              }}
            />
          ) : null}
        </KpiElementListContent>
      );
    });

  // Liste de kpi ajouter au contrat lors de la création du contrat
  const KpiAddedBlocList = fieldAddedKpi.map((kpiAdded, index) => {
    return (
      <KpiAddedList data-testid="KpiAddedList" key={kpiAdded.id}>
        <KpiAddedListContent data-testid="KpiAddedListContent">
          <KpiAddedElementContentLeft data-testid="KpiAddedElementContentLeft">
            <KpiAddedName data-testid="KpiAddedName">
              <h3>{kpiAdded.kpi.nom}</h3>
              <KpiAddedInfoComplementaire data-testid="KpiAddedInfoComplementaire">
                {kpiAdded.informations}
              </KpiAddedInfoComplementaire>
            </KpiAddedName>

            <Tooltips
              content={kpiAdded.kpi.description}
              data-testid="Tooltips"
            />
          </KpiAddedElementContentLeft>

          <KpiAddedObjectif data-testid="KpiAddedObjectif">
            <h6>Objectif</h6>
            <p>{kpiAdded.objectif}</p>
          </KpiAddedObjectif>
        </KpiAddedListContent>

        <Button
          label="Supprimer"
          icon={IconTrash}
          type="button"
          onClick={() => {
            // Permet de remettre les valeur dans la saisi pour faire une modification.
            const KpiToRemove = getValues(`addedKpi.${index}`);
            setValue(
              `Kpi.${KpiToRemove.kpi.kpi_id}.objectif`,
              KpiToRemove.objectif
            );
            setValue(
              `Kpi.${KpiToRemove.kpi.kpi_id}.informations`,
              KpiToRemove.informations
            );
            // on supprime le KPI de la liste des KPI à ajouter
            removeAddedKpi(index);
            // On met à jour le local storage si on aucun contrat n'est encore créer
            if (!currentContratIsEncours) {
              localStorage.setItem(
                `KPI_added_${campagne.campagne_id}_${fournisseur?.fournisseur_id}_${currentContremarque?.value}`,
                JSON.stringify(getValues("addedKpi"))
              );
            }
          }}
          data-testid="Button"
        />
      </KpiAddedList>
    );
  });

  const handleNegate = (): void => {
    campagne.kpis.forEach(kpi => {
      setValue(`Kpi.${kpi.kpi_id}.objectif`, undefined);
      setValue(`Kpi.${kpi.kpi_id}.informations`, undefined);
      setValue(`KpiError.${kpi.kpi_id}.message`, undefined);
    });
    if (!currentContratIsEncours) {
      // On retire les KPI ajoutés du formulaire
      removeAddedKpi();
      // On retire les KPI ajoutés du local storage si on aucun contrat n'est encore créer
      localStorage.removeItem(
        `KPI_added_${campagne.campagne_id}_${fournisseur?.fournisseur_id}_${currentContremarque?.value}`
      );

      setIsCreationContratStatut(false);
    } else {
      const {contrat, ...rest} = fournisseurAndCampagne;
      setFournisseurAndCampagne({...rest, contrat: contrat ? contrat : null});
      handleGetAddedKpi(contrat ? contrat : null);
    }
  };
  const onSubmit = async (data: {addedKpi: AddedKpi[]}): Promise<void> => {
    const contratDownloader = async (
      currentContrat: Contrat,
      currentContremarque?: OptionNameValue
    ): Promise<void> => {
      const fichierToDownload = currentContrat.fichiers.find(
        fichier => fichier.classe === "zip-contrat-annexe"
      );
      if (fichierToDownload) {
        await downloadFichier(
          API,
          fichierToDownload.fichier_id,
          currentContremarque?.value
        );
      }
    };
    DownloadContratLoadingState.executeWithLoading(async () => {
      try {
        // Si on a pas de contrat on le créer
        if (contrat === null && fournisseurId && campagne.campagne_id) {
          const contratToCreate = {
            fournisseur_id: parseInt(fournisseurId),
            campagne_id: campagne.campagne_id,
            kpis: data.addedKpi?.map((kpi: AddedKpi) => ({
              kpi_id: parseFloat(kpi.kpi.kpi_id?.toString() || "0"),
              objectif: parseFloat(kpi.objectif?.toString() || "0"),
              informations: kpi.informations || "",
            })),
          };
          const newContrat = await createContrat(
            API,
            contratToCreate,
            currentContremarque.value
          );

          setFournisseurAndCampagne({
            ...fournisseurAndCampagne,
            contrat: newContrat,
            campagne: newContrat.campagne,
          });

          await contratDownloader(newContrat, currentContremarque);
        }
        // Sinon on le modifie
        else if (contrat !== null) {
          const contratToUpdate = {
            statut: contrat.statut,
            kpis: data.addedKpi?.map((kpi: AddedKpi) => ({
              kpi_id: parseFloat(kpi.kpi.kpi_id?.toString() || "0"),
              objectif: parseFloat(kpi.objectif?.toString() || "0"),
              informations: kpi.informations || "",
            })),
          };
          const updatedContrat = await updateContrat(
            API,
            contratToUpdate,
            contrat.contrat_id,
            currentContremarque.value
          );
          setFournisseurAndCampagne({
            ...fournisseurAndCampagne,
            contrat: updatedContrat,
            campagne: updatedContrat.campagne,
          });
          await contratDownloader(updatedContrat, currentContremarque);
        }
        // Scroll vers le bloc de téléchargement du contrat lors de l'enregistrement du contrat
        if (downloadContratRef.current) {
          downloadContratRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        localStorage.removeItem(
          `KPI_added_${campagne.campagne_id}_${fournisseur?.fournisseur_id}_${currentContremarque?.value}`
        );
      } catch (error: any) {
        console.error(error);
      }
    });
  };

  const FournisseurHeaderChildrenVueAdherent = (): ReactElement | null => {
    if (currentContratIsEncours) {
      return (
        <ButtonNoClicableStyled
          data-testid="ButtonNoClicableStyled"
          label="ATTENTE DE CONTRAT"
          variant="light_primary"
          onClick={() => {
            return;
          }}
        />
      );
    } else {
      // si la campagne est terminée, on ne rend pas le bouton d'adhérer au programme
      if (new Date(campagne?.fin) < new Date()) {
        return null;
      }
      if (!isCreationContratStatut) {
        if (
          new Date(campagne?.fin) > new Date() &&
          campagne?.bonus?.length > 0
        ) {
          return (
            <ButtonStyled
              data-testid="ButtonStyled"
              label="ADHÉRER AU PROGRAMME"
              onClick={handleStartAdhesion}
            />
          );
        } else {
          return null;
        }
      } else {
        return (
          <ButtonNoClicableStyled
            data-testid="ButtonNoClicableStyled"
            label="ADHÉSION EN COURS"
            variant="negatif_primary"
            onClick={() => {
              return;
            }}
          />
        );
      }
    }
  };
  const FournisseurHeaderChildrenVueCollaborateur = (): ReactElement => {
    return <p>Test</p>;
  };
  return (
    <>
      <FournisseurHeader fournisseurInfo={fournisseur} bonus={campagne?.bonus}>
        {userType === "Adherent"
          ? FournisseurHeaderChildrenVueAdherent()
          : null}
        {userType === "Collaborateur"
          ? FournisseurHeaderChildrenVueCollaborateur()
          : null}
      </FournisseurHeader>

      {isCreationContratStatut && contrat !== null ? (
        <div ref={downloadContratRef}>
          {getValues("contrat-signe-isUploaded") ? (
            <FichierEnvoye
              fileName="Contrat signé"
              fileNameComplement={getValues("contrat-signe-upload")?.[0]?.name}
              icon={<IconPdf />}
              handleRemoveFile={() =>
                handleRemoveFile(
                  getValues("contrat-signe-fichier_id"),
                  "contrat-signe"
                )
              }
            />
          ) : (
            <UploadContrat
              name="contrat-signe-upload"
              typeDocument="contrat-signe"
              currentContremarque={currentContremarque}
            />
          )}
          {getValues("annexe-isUploaded") ? (
            <FichierEnvoye
              fileName="Annexe"
              fileNameComplement={getValues("annexe-upload")?.[0]?.name}
              icon={<IconPdf />}
              handleRemoveFile={() =>
                handleRemoveFile(getValues("annexe-fichier_id"), "annexe")
              }
            />
          ) : (
            <UploadContrat
              name="annexe-upload"
              typeDocument="annexe"
              currentContremarque={currentContremarque}
            />
          )}
        </div>
      ) : null}
      <form onSubmit={handleSubmit(onSubmit)}>
        <BlocContent>
          <Title title={`KPI du programme ${fournisseur?.nom}`}>
            {/* Si on est en création de contrat et que l'utilisateur est un adhérent, on affiche le bouton d'adhérer au programme
            Seul les adhérents peuvent créer un contrat */}
            {!isCreationContratStatut &&
            userType === "Adherent" &&
            new Date(campagne?.fin) > new Date() &&
            campagne?.bonus?.length > 0 ? (
              <Button
                label="Adhérer au programme"
                onClick={handleStartAdhesion}
              />
            ) : null}
          </Title>
          {KpiList}
        </BlocContent>
        {KpiAddedBlocList?.length ? (
          <BlocContent>
            <div data-testid="KpiAddedBloc">
              <Title
                title={`${KpiAddedBlocList?.length} KPI Ajouté${KpiAddedBlocList?.length > 1 ? "s" : ""}`}
              />
              {KpiAddedBlocList}
            </div>
            {/* Si on est en création de contrat et que l'utilisateur est un adhérent, on affiche les boutons d'annulation et d'enregistrement 
              Seul les adhérents peuvent créer un contrat*/}
            {isCreationContratStatut && userType === "Adherent" ? (
              <StyledFooter data-testid="StyledFooter">
                <Button
                  label="ANNULER"
                  icon={IconCross}
                  variant="negatif_secondary"
                  type="button"
                  onClick={handleNegate}
                />
                <Button
                  label="ENREGISTRER ET TELECHARGER LE CONTRAT"
                  icon={
                    DownloadContratLoadingState.isLoading ? (
                      <Loader isIcon={true} color="white" />
                    ) : (
                      IconContrat
                    )
                  }
                  disabled={DownloadContratLoadingState.isLoading}
                  variant="secondary"
                  onClick={() => {
                    return;
                  }}
                />
              </StyledFooter>
            ) : null}
          </BlocContent>
        ) : null}
      </form>
    </>
  );
}
